"use client";

import { regionFromPathname, type Region } from "@/lib/region";
import { resolveRichTextHref } from "@/lib/storyblok-link";
import { usePublicPathname } from "@/lib/public-pathname";

type RichTextMark = {
  type: string;
  attrs?: { href?: string; target?: string; linktype?: string };
};
type RichTextNode = {
  type: string;
  text?: string;
  content?: RichTextNode[];
  marks?: RichTextMark[];
};
export type RichTextDoc = { type: "doc"; content?: RichTextNode[] };

export type RichTextValue = string | RichTextDoc | null | undefined;

export function richTextHasContent(value: RichTextValue): boolean {
  if (!value) return false;
  if (typeof value === "string") return value.trim() !== "";
  return nodeHasText(value);
}

function nodeHasText(node: RichTextNode | undefined): boolean {
  if (!node) return false;
  if (node.type === "text") return !!node.text?.trim();
  return (node.content ?? []).some(nodeHasText);
}

function renderNode(node: RichTextNode, key: number, region?: Region): React.ReactNode {
  switch (node.type) {
    case "paragraph":
      return <p key={key}>{node.content?.map((n, i) => renderNode(n, i, region))}</p>;
    case "heading":
      return (
        <h3 key={key} className="text-h6 text-teal-08 font-bold">
          {node.content?.map((n, i) => renderNode(n, i, region))}
        </h3>
      );
    case "hard_break":
      return <br key={key} />;
    case "bullet_list":
      return (
        <ul key={key} className="ml-5 list-disc space-y-1">
          {node.content?.map((n, i) => renderNode(n, i, region))}
        </ul>
      );
    case "ordered_list":
      return (
        <ol key={key} className="ml-5 list-decimal space-y-1">
          {node.content?.map((n, i) => renderNode(n, i, region))}
        </ol>
      );
    case "list_item":
      return (
        <li key={key}>
          {node.content?.map((n, i) =>
            n.type === "paragraph"
              ? n.content?.map((c, j) => renderNode(c, j, region))
              : renderNode(n, i, region),
          )}
        </li>
      );
    case "text": {
      let el: React.ReactNode = node.text ?? "";
      for (const mark of node.marks ?? []) {
        if (mark.type === "bold") el = <strong>{el}</strong>;
        else if (mark.type === "italic") el = <em>{el}</em>;
        else if (mark.type === "link" && mark.attrs?.href && (node.text ?? "").trim()) {
          const href = resolveRichTextHref(mark.attrs.href, region);
          const isExternal = /^https?:\/\//i.test(href);
          const target = isExternal ? "_blank" : mark.attrs.target;
          el = (
            <a
              href={href}
              target={target}
              rel={target === "_blank" ? "noopener noreferrer" : undefined}
              className="text-teal-08 underline underline-offset-2 hover:opacity-80"
            >
              {el}
            </a>
          );
        }
      }
      return <span key={key}>{el}</span>;
    }
    default:
      return node.content?.map((n, i) => renderNode(n, i, region)) ?? null;
  }
}

export function RichText({ value, className }: { value: RichTextValue; className?: string }) {
  const region = regionFromPathname(usePublicPathname() ?? "/");
  if (!richTextHasContent(value)) return null;

  if (typeof value === "string") {
    const paragraphs = value
      .split(/\n\s*\n/)
      .map((p) => p.trim())
      .filter(Boolean);
    return (
      <div className={className}>
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    );
  }

  return <div className={className}>{value?.content?.map((n, i) => renderNode(n, i, region))}</div>;
}
